import {
  ComputedResults,
  OpportunityResultRow,
  OpportunityScore,
  ScoringState,
  Theme,
} from './api';

export type SortMode = 'priority-desc' | 'gain-desc' | 'pain-asc' | 'pain-desc' | 'theme';
export type OpportunityStatus = 'active' | 'inflight' | 'deferred' | 'shipped';

export interface RankerFilters {
  source: string;
  strategic: string;
  theme: string;
  sortBy: SortMode;
  hideInactive: boolean;
  top10Only: boolean;
  showDeferred: boolean;
}

export const DEFAULT_FILTERS: RankerFilters = {
  source: 'all',
  strategic: 'all',
  theme: 'all',
  sortBy: 'priority-desc',
  hideInactive: true,
  top10Only: false,
  showDeferred: false,
};

export const STRATEGIC_LABELS: Record<string, string> = {
  new_logo: 'New Logo / Market Moving',
  retention: 'Retention / Expansion',
  efficiency: 'Time-to-Value / Unit Economics',
};

export const SOURCE_LABELS: Record<string, string> = {
  LR: 'Lightning Round',
  Lib: 'Library',
  RW: 'User-added',
  PF: 'THL Portfolio',
  DEF: 'Deferred',
};

export const STATUS_OPTIONS: Array<{ value: OpportunityStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'inflight', label: 'In-flight' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'shipped', label: 'Shipped' },
];

export function statusLabel(status: string | null | undefined): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status ?? 'Active';
}

const DEFAULT_GAIN_WEIGHTS = [0.4, 0.2, 0.15, 0.15, 0.1];
const DEFAULT_PAIN_WEIGHTS = [0.2, 0.15, 0.15, 0.1, 0.1, 0.1, 0.1, 0.1];
const DEFAULT_GAIN_LABELS = ['Financial ROI', 'KPI Proximity', 'Strategic Diff.', 'Scalability', 'Speed to Value'];
const DEFAULT_PAIN_LABELS = [
  'Data Avail.',
  'Eng. Complexity',
  'Client Readiness',
  'Resource',
  'Process Maturity',
  'Integration',
  'Risk/Compliance',
  'Testability',
];
const SOURCE_OVERRIDE_KEYS = [
  'rank',
  'source_rank',
  'source_gain_index',
  'source_pain_index',
  'source_priority_score',
] as const;

interface ComputeRowsOptions {
  recomputeIds?: Set<string>;
}

export function normalizeThemes(themes: ScoringState['themes']): Theme[] {
  if (Array.isArray(themes)) {
    return themes.map((theme) => ({ ...theme, id: theme.id }));
  }
  if (themes && typeof themes === 'object') {
    return Object.entries(themes).map(([id, theme]) => ({ ...theme, id }));
  }
  return [];
}

export function gainLabels(state: ScoringState): string[] {
  return state.gain_labels?.length ? state.gain_labels : DEFAULT_GAIN_LABELS;
}

export function painLabels(state: ScoringState): string[] {
  return state.pain_labels?.length ? state.pain_labels : DEFAULT_PAIN_LABELS;
}

export function gainWeights(state: ScoringState): number[] {
  return normalizeWeights(state.gain_weights, DEFAULT_GAIN_WEIGHTS, gainLabels(state).length);
}

export function painWeights(state: ScoringState): number[] {
  return normalizeWeights(state.pain_weights, DEFAULT_PAIN_WEIGHTS, painLabels(state).length);
}

export function computeRows(
  state: ScoringState,
  savedResults?: ComputedResults,
  options: ComputeRowsOptions = {}
): OpportunityResultRow[] {
  const recomputeIds = options.recomputeIds ?? new Set<string>();
  const savedById = new Map((savedResults?.ranked_opportunities ?? []).map((row) => [row.id, row]));
  const themeById = new Map(normalizeThemes(state.themes).map((theme) => [String(theme.id), theme.name]));
  const opportunitiesById = new Map(state.opportunities.map((opportunity) => [opportunity.id, opportunity]));
  const rows = state.opportunities.map((opportunity, index) => {
    const saved = savedById.get(opportunity.id);
    const details = state.details?.[opportunity.id];
    const base = {
      id: opportunity.id,
      name: opportunity.name,
      theme_id: opportunity.theme,
      theme_name: themeById.get(String(opportunity.theme)) ?? saved?.theme_name ?? null,
      source: opportunity.source ?? saved?.source ?? null,
      status: opportunity.status ?? saved?.status ?? 'active',
      submitter: opportunity.submitter ?? details?.submitter ?? saved?.submitter ?? null,
      description: opportunity.desc ?? details?.description ?? saved?.description ?? null,
      strategic_tags: state.strategic_tags?.[opportunity.id] ?? saved?.strategic_tags ?? [],
      gain: opportunity.gain,
      pain: opportunity.pain,
      import_order: opportunity.import_order ?? index + 1,
      source_rank: opportunity.source_rank ?? opportunity.rank ?? null,
      source_gain_index: opportunity.source_gain_index ?? null,
      source_pain_index: opportunity.source_pain_index ?? null,
      source_priority_score: opportunity.source_priority_score ?? null,
    };
    if (saved && !recomputeIds.has(opportunity.id)) {
      return {
        ...saved,
        ...base,
        source_rank: base.source_rank ?? saved.source_rank ?? null,
        priority_score: saved.priority_score ?? saved.composite_score,
        composite_score: saved.priority_score ?? saved.composite_score,
      };
    }
    return scoredRow(base, state);
  });

  const savedRows = savedResults?.ranked_opportunities ?? [];
  if (savedRows.length > 0 && recomputeIds.size === 0) {
    const hydrated = savedRows.map((saved, index) => {
      const opportunity = opportunitiesById.get(saved.id);
      if (!opportunity) return saved;
      const details = state.details?.[opportunity.id];
      return {
        ...saved,
        name: opportunity.name,
        theme_id: opportunity.theme,
        theme_name: themeById.get(String(opportunity.theme)) ?? saved.theme_name ?? null,
        source: opportunity.source ?? saved.source ?? null,
        status: opportunity.status ?? saved.status ?? 'active',
        submitter: opportunity.submitter ?? details?.submitter ?? saved.submitter ?? null,
        description: opportunity.desc ?? details?.description ?? saved.description ?? null,
        strategic_tags: state.strategic_tags?.[opportunity.id] ?? saved.strategic_tags ?? [],
        gain: opportunity.gain,
        pain: opportunity.pain,
        import_order: opportunity.import_order ?? saved.import_order ?? index + 1,
        source_rank: opportunity.source_rank ?? opportunity.rank ?? saved.source_rank ?? null,
        source_gain_index: opportunity.source_gain_index ?? saved.source_gain_index ?? null,
        source_pain_index: opportunity.source_pain_index ?? saved.source_pain_index ?? null,
        source_priority_score: opportunity.source_priority_score ?? saved.source_priority_score ?? null,
        priority_score: saved.priority_score ?? saved.composite_score,
        composite_score: saved.priority_score ?? saved.composite_score,
      };
    });
    const seen = new Set(hydrated.map((row) => row.id));
    state.opportunities.forEach((opportunity, index) => {
      if (!seen.has(opportunity.id)) {
        hydrated.push(scoredRow({
          id: opportunity.id,
          name: opportunity.name,
          theme_id: opportunity.theme,
          theme_name: themeById.get(String(opportunity.theme)) ?? null,
          source: opportunity.source ?? null,
          status: opportunity.status ?? 'active',
          submitter: opportunity.submitter ?? null,
          description: opportunity.desc ?? null,
          strategic_tags: state.strategic_tags?.[opportunity.id] ?? [],
          gain: opportunity.gain,
          pain: opportunity.pain,
          import_order: opportunity.import_order ?? index + 1,
          source_rank: opportunity.source_rank ?? null,
          source_gain_index: opportunity.source_gain_index ?? null,
          source_pain_index: opportunity.source_pain_index ?? null,
          source_priority_score: opportunity.source_priority_score ?? null,
        }, state));
      }
    });
    return withActiveRanks(hydrated);
  }

  rows.sort(comparePriority);
  rows.forEach((row, index) => {
    row.rank = row.source_rank ?? index + 1;
  });
  return withActiveRanks(rows);
}

function scoredRow(base: Omit<OpportunityResultRow, 'gain_score' | 'pain_score' | 'composite_score'>, state: ScoringState): OpportunityResultRow {
  const computedGain = indexScore(base.gain, gainWeights(state));
  const computedPain = indexScore(base.pain, painWeights(state));
  const sourceGain = scorecardValue(base.source_gain_index);
  const sourcePain = scorecardValue(base.source_pain_index);
  const sourcePriority = scorecardValue(base.source_priority_score);
  const gain_score = sourceGain ?? computedGain;
  const pain_score = sourcePain ?? computedPain;
  const priority_score = sourcePriority ?? priorityScore(gain_score, pain_score);
  const hasSourceIndex = (
    sourceGain !== undefined
    && sourceGain !== null
  ) || (
    sourcePain !== undefined
    && sourcePain !== null
  );
  const priority_score_source = sourcePriority !== undefined && sourcePriority !== null
    ? 'source_priority'
    : hasSourceIndex
      ? 'source_indexes'
      : 'computed';
  return {
    ...base,
    gain_score,
    pain_score,
    source_gain_index: sourceGain,
    source_pain_index: sourcePain,
    source_priority_score: sourcePriority,
    priority_score,
    priority_score_source,
    priority_score_note: priority_score_source === 'source_indexes' ? 'Derived from uploaded gain/pain indexes.' : null,
    composite_score: priority_score,
    composite_band: priorityBand(priority_score),
  };
}

function withActiveRanks(rows: OpportunityResultRow[]): OpportunityResultRow[] {
  const activeRows = rows.filter((row) => row.status === 'active');
  const top10 = new Set(activeRows.slice(0, 10).map((row) => row.id));
  activeRows.forEach((row, index) => {
    row.active_rank = index + 1;
  });
  rows.forEach((row) => {
    row.is_top_10_active = top10.has(row.id);
    row.active_rank = row.status === 'active' ? row.active_rank ?? null : null;
  });
  return rows;
}

export function applyFilters(rows: OpportunityResultRow[], filters: RankerFilters): OpportunityResultRow[] {
  const filtered = rows.filter((row) => {
    if (filters.source !== 'all' && row.source !== filters.source) return false;
    if (filters.theme !== 'all' && String(row.theme_id ?? '') !== filters.theme) return false;
    if (filters.strategic !== 'all' && !(row.strategic_tags ?? []).includes(filters.strategic)) return false;
    if (filters.hideInactive && (row.status === 'shipped' || row.status === 'inflight')) return false;
    if (!filters.showDeferred && row.status === 'deferred') return false;
    if (filters.top10Only && !row.is_top_10_active) return false;
    return true;
  });
  return [...filtered].sort((a, b) => compareByMode(a, b, filters.sortBy));
}

export function updateScore(
  state: ScoringState,
  opportunityId: string,
  kind: 'gain' | 'pain',
  index: number,
  nextValue: number
): ScoringState {
  const bounded = Math.max(1, Math.min(5, Math.round(nextValue)));
  return {
    ...state,
    opportunities: state.opportunities.map((opportunity) =>
      opportunity.id === opportunityId
        ? clearSourceOverrides({
            ...opportunity,
            [kind]: opportunity[kind].map((score, scoreIndex) => (scoreIndex === index ? bounded : score)),
          }, true)
        : opportunity
    ),
  };
}

export function clearSourceOverrides<T extends OpportunityScore>(opportunity: T, markCleared = false): T {
  const next = { ...opportunity };
  SOURCE_OVERRIDE_KEYS.forEach((key) => {
    delete next[key];
  });
  if (markCleared) {
    next.source_overrides_cleared = true;
  }
  return next;
}

export function updateStatus(
  state: ScoringState,
  opportunityId: string,
  status: OpportunityStatus
): ScoringState {
  return {
    ...state,
    opportunities: state.opportunities.map((opportunity) =>
      opportunity.id === opportunityId ? { ...opportunity, status } : opportunity
    ),
  };
}

export function opportunityById(state: ScoringState, id: string): OpportunityScore | undefined {
  return state.opportunities.find((opportunity) => opportunity.id === id);
}

export function displaySource(source: string | null | undefined): string {
  if (!source) return 'Unknown';
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source];
  const [filename, slides] = source.split('; slide(s)');
  const cleaned = filename
    .replace(/\.pptx$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (slides) return `${cleaned} · slides ${slides.trim()}`;
  return cleaned || source;
}

function indexScore(scores: number[] | undefined, weights: number[]): number {
  const weighted = (scores ?? []).slice(0, weights.length).reduce((sum, value, index) => sum + value * weights[index], 0);
  return Math.min(100, Math.max(0, Math.round(((weighted - 1) / 4) * 100)));
}

function priorityScore(gainIndex: number, painIndex: number): number {
  return Number(((0.70 * gainIndex) + (0.30 * (100 - painIndex))).toFixed(2));
}

function scorecardValue(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

function normalizeWeights(raw: number[] | undefined, fallback: number[], expectedLength: number): number[] {
  if (expectedLength <= 0) return [];
  const fallbackValues = fallbackWeights(fallback, expectedLength);
  const values = Array.from({ length: expectedLength }, (_, index) => {
    const value = raw?.[index];
    return finiteWeight(value) ?? fallbackValues[index];
  });
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return fallbackValues;
  return values.map((value) => value / total);
}

function fallbackWeights(fallback: number[], expectedLength: number): number[] {
  if (fallback.length === expectedLength) {
    const values = fallback.map(finiteWeight);
    if (values.every((value) => value !== null)) {
      const total = values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
      if (total > 0) return values.map((value) => (value ?? 0) / total);
    }
  }
  return Array.from({ length: expectedLength }, () => 1 / expectedLength);
}

function finiteWeight(value: number | undefined): number | null {
  if (value === undefined || !Number.isFinite(value) || value < 0) return null;
  return Math.min(value, 100);
}

function compareByMode(a: OpportunityResultRow, b: OpportunityResultRow, mode: SortMode): number {
  if (mode === 'gain-desc') return b.gain_score - a.gain_score || comparePriority(a, b);
  if (mode === 'pain-asc') return a.pain_score - b.pain_score || comparePriority(a, b);
  if (mode === 'pain-desc') return b.pain_score - a.pain_score || comparePriority(a, b);
  if (mode === 'theme') {
    return String(a.theme_name ?? '').localeCompare(String(b.theme_name ?? '')) || comparePriority(a, b);
  }
  return comparePriority(a, b);
}

function comparePriority(a: OpportunityResultRow, b: OpportunityResultRow): number {
  if (a.source_rank !== undefined && a.source_rank !== null && b.source_rank !== undefined && b.source_rank !== null) {
    return a.source_rank - b.source_rank || (a.import_order ?? 0) - (b.import_order ?? 0) || a.id.localeCompare(b.id);
  }
  if (a.source_rank !== undefined && a.source_rank !== null) return -1;
  if (b.source_rank !== undefined && b.source_rank !== null) return 1;
  return priorityValue(b) - priorityValue(a) || a.pain_score - b.pain_score || (a.import_order ?? 0) - (b.import_order ?? 0) || a.id.localeCompare(b.id);
}

function priorityValue(row: OpportunityResultRow): number {
  return row.priority_score ?? row.composite_score;
}

function priorityBand(value: number): string {
  if (value >= 40) return 'high';
  if (value >= 20) return 'medium';
  return 'low';
}
